import { useContext } from 'react';
import { Box, Button, Dialog, DialogBody, DialogFooter, Link, Notification, Text } from '@bitrise/bitkit';
import useActivateBuildCacheSubscriptionEvent from '@/components/BuildPerformance/hooks/useActivateBuildCacheSubscriptionEvent';
import useBuildCacheSubscriptionRequest from '@/components/BuildPerformance/hooks/useBuildCacheSubscriptionRequest';
import { GlobalPropsContext } from '@/components/page/GlobalPropsContext';
import { GlobalPropsWithAccount } from '@/models/GlobalProps';

export interface BuildCacheRequestSubscriptionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  source: string;
}

const RequestSubscriptionDialog = ({
  isOpen,
  onClose,
  source,
}: BuildCacheRequestSubscriptionDialogProps): JSX.Element | null => {
  const { account } = useContext(GlobalPropsContext) as GlobalPropsWithAccount;

  const { submit: trackActivateBuildCacheSubscription } = useActivateBuildCacheSubscriptionEvent();

  const {
    submit: requestSubscription,
    isPending: isRequestSubscriptionLoading,
    isError: isRequestSubscriptionError,
  } = useBuildCacheSubscriptionRequest(account.slug, source, () => {
    trackActivateBuildCacheSubscription(account.slug);
    window.location.reload();
  });

  return (
    <Dialog
      isOpen={isOpen}
      title="Send request to activate subscription"
      onClose={onClose}
      data-testid="requestBuildCacheSubscriptionDialog"
      data-clarity-unmask="true"
    >
      <DialogBody>
        <Box display="flex" flexDir="column" gap="1.5rem">
          <Box display="flex" flexDir="column" gap=".5rem">
            <Text textStyle="body/lg/regular" textColor="text/secondary">
              Build Cache plan
            </Text>
            <Text textStyle="heading/h3" textColor="text/body">
              From $600/month
            </Text>
          </Box>
          <Text textStyle="body/lg/regular" textColor="text/body">
            Includes 10 seats and 100 GB high performance storage.
          </Text>
          <Box display="flex" flexDir="column">
            <Text textStyle="body/md/regular" textColor="text/secondary">
              Find out more about our pricing plans{' '}
              <Link href="https://bitrise.io/plans-pricing#remote-build-cache" colorScheme="purple" isExternal>
                here
              </Link>
              .
            </Text>
            <Text textStyle="body/md/regular" textColor="text/secondary">
              To start your subscription, please send us a request and we will reach out to you.
            </Text>
          </Box>
          {isRequestSubscriptionError && <Notification status="error">Failed to activate trial</Notification>}
        </Box>
      </DialogBody>
      <DialogFooter>
        <Button variant="secondary" size="lg" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" size="lg" onClick={requestSubscription} isLoading={isRequestSubscriptionLoading}>
          Send request
        </Button>
      </DialogFooter>
    </Dialog>
  );
};

export default RequestSubscriptionDialog;
